import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { getApiBaseUrl } from "../config/apiBase";
import { apiFetch, getConsoleToken, setConsoleToken } from "../config/apiFetch";
import { postAuthLogout } from "../api/auditLogApi";

export type ConsoleRole = "superadmin" | "admin";

export type ConsoleUser = {
  id: string;
  email: string;
  displayName: string;
  role: ConsoleRole;
  permissions: string[];
  /** When set, inventory APIs are limited to these store/yard ids (see Console Users → Inventory store access). */
  inventoryStoreYardIds?: string[] | null;
};

function normalizeConsoleRole(role: string | undefined): ConsoleRole {
  return String(role ?? "").trim().toLowerCase() === "superadmin" ? "superadmin" : "admin";
}

function normalizeConsoleUser(u: ConsoleUser): ConsoleUser {
  return { ...u, role: normalizeConsoleRole(u.role) };
}

export type AuthContextValue = {
  loading: boolean;
  authRequired: boolean;
  user: ConsoleUser | null;
  refresh: () => Promise<void>;
  login: (email: string, password: string) => Promise<ConsoleUser>;
  logout: () => void;
  can: (permission: string) => boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [authRequired, setAuthRequired] = useState(false);
  const [user, setUser] = useState<ConsoleUser | null>(null);

  const refresh = useCallback(async () => {
    const base = getApiBaseUrl();
    const cfgRes = await fetch(`${base}/api/auth/config`);
    const cfg = (await cfgRes.json()) as { authRequired?: boolean };
    const required = Boolean(cfg.authRequired);
    setAuthRequired(required);

    if (!required) {
      setUser({
        id: "local-bypass",
        email: "",
        displayName: "Console user",
        role: "superadmin",
        permissions: [],
        inventoryStoreYardIds: null,
      });
      setLoading(false);
      return;
    }

    const token = getConsoleToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    const meRes = await apiFetch(`${base}/api/auth/me`);
    if (!meRes.ok) {
      setConsoleToken(null);
      setUser(null);
      setLoading(false);
      return;
    }
    const me = (await meRes.json()) as { user: ConsoleUser | null };
    setUser(me.user ? normalizeConsoleUser(me.user) : null);
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      const base = getApiBaseUrl();
      const res = await fetch(`${base}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const j = (await res.json().catch(() => ({}))) as {
        token?: string;
        user?: ConsoleUser;
        error?: string;
      };
      if (!res.ok) {
        throw new Error(j.error || `Sign-in failed (${res.status})`);
      }
      if (!j.token || !j.user) throw new Error("Invalid server response");
      setConsoleToken(j.token);
      await refresh();
      return normalizeConsoleUser(j.user);
    },
    [refresh],
  );

  const logout = useCallback(() => {
    void postAuthLogout().catch(() => {});
    setConsoleToken(null);
    setUser(null);
  }, []);

  const can = useCallback(
    (permission: string) => {
      if (!authRequired) return true;
      const u = user;
      if (!u) return false;
      if (normalizeConsoleRole(u.role) === "superadmin") return true;
      return u.permissions.includes(permission);
    },
    [authRequired, user],
  );

  const value = useMemo(
    () => ({ loading, authRequired, user, refresh, login, logout, can }),
    [loading, authRequired, user, refresh, login, logout, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
